import Autocomplete from "@mui/material/Autocomplete";
import Icon from "@mui/material/Icon";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import DataTableBodyCell from "components/DataTable/DataTableBodyCell";
import DataTableHeadCell from "components/DataTable/DataTableHeadCell";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDPagination from "components/MDPagination";
import MDTypography from "components/MDTypography";
import PropTypes from "prop-types";
import { useMemo } from "react";
import useGlobalStore from "store/useGlobalStore";

function DataTable({
  entriesPerPage,
  canSearch,
  showTotalEntries,
  table,
  pagination,
  noEndBorder,
  page,
  pageSize,
  totalCount,
  sortBy,
  sortType,
  onPageChange,
  onPageSizeChange,
  onSortChange,
  onSearch,
  searchValue,
  onClickRow
}) {
  const columns = useMemo(() => table.columns, [table]);
  const data = useMemo(() => table.rows, [table]);
  const { prevSelectedRow } = useGlobalStore();
  const entries = entriesPerPage.entries
    ? entriesPerPage.entries.map((el) => el.toString())
    : ["10", "20", "30", "50"];

  // Pagination controls
  const totalPages = Math.ceil(totalCount / pageSize);

  // Scroll to previous selected row on mount
  const scrollToPrevSelectedRow = () => {
    if (prevSelectedRow && window.location.pathname === prevSelectedRow.pathname) {
      const matchingRowIndex = data.findIndex(row => row?.oriData?.id === prevSelectedRow.id);
      if (matchingRowIndex !== -1) {
        setTimeout(() => {
          const tableRows = document.querySelectorAll('tbody tr');
          const targetRow = tableRows[matchingRowIndex];
          if (targetRow) {
            targetRow.scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            });
          }
        }, 50);
      }
    }
  };

  // Call scroll function when data changes
  useMemo(() => {
    scrollToPrevSelectedRow();
  }, [data, prevSelectedRow]);

  const handleHightlight = (row) => {
    if (row?.oriData?.inspection_checklist_failed_item?.length > 0) {
      return "#FDDACE"; // Highlight color for rows with inspection errors
    } else if (prevSelectedRow && row?.oriData?.id === prevSelectedRow.id && window.location.pathname === prevSelectedRow.pathname) {
      return "#F5E6FC"; // Highlight color for the prev entered row
    }
    return "inherit"; // Default background color
  }

  // Sorting handler
  const handleSort = (column) => {
    if (!column.sortable || !column.id) return;
    let direction = "asc";
    if (sortBy === column.id && sortType === "asc") direction = "desc";
    onSortChange && onSortChange(column.id, direction);
  };

  return (
    <TableContainer sx={{ boxShadow: "none" }}>
      {canSearch ? (
        <MDBox display="flex" justifyContent="space-between" alignItems="center" p={3}>
          {canSearch && (
            <MDBox width="12rem" ml="auto">
              <MDInput
                placeholder="Search..."
                value={searchValue}
                size="small"
                fullWidth
                onChange={({ currentTarget }) => {
                  onSearch && onSearch(currentTarget.value);
                }}
              />
            </MDBox>
          )}
        </MDBox>
      ) : null}
      <Table>
        <MDBox component="thead">
          <TableRow>
            {columns.map((column, idx) => (
              <DataTableHeadCell
                key={idx}
                width={column.width ? column.width : "auto"}
                align={column.align ? column.align : "left"}
                sorted={!column.sortable ? null :
                  column.sortable && sortBy === column.id
                    ? sortType === "asc"
                      ? "asc"
                      : "desc"
                    : "none"
                }
                onClick={() => handleSort(column)}
                style={{ cursor: column.sortable ? "pointer" : "default" }}
              >
                {typeof column.Header === 'function' ? column.Header() : column.Header}
              </DataTableHeadCell>
            ))}
          </TableRow>
        </MDBox>
        <TableBody>
          {data.map((row, key) => (
            <TableRow
              key={key}
              sx={{
                transition: "background-color 0.2s",
                //special highlight for rows with inspection errors
                backgroundColor: handleHightlight(row),
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              }}
              onClick={onClickRow ? () => onClickRow(row, key) : undefined}
              style={onClickRow ? { cursor: "pointer" } : undefined}
            >
              {columns.map((col, idx) => (
                <DataTableBodyCell
                  key={idx}
                  noBorder={noEndBorder && data.length - 1 === key}
                  align={col.align ? col.align : "left"}
                  color
                >
                  {col.Cell ? col.Cell({ row, rowIndex: key }) : row[col.accessor]}
                </DataTableBodyCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <MDBox
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        p={!showTotalEntries && totalPages === 1 ? 0 : 3}
      >
        {showTotalEntries && (
          <MDBox mb={{ xs: 3, sm: 0 }}>
            <MDTypography variant="button" color="secondary" fontWeight="regular">
              Showing {(page - 1) * pageSize + 1} to{" "}
              {Math.min(page * pageSize, totalCount)} of {totalCount} entries
            </MDTypography>
          </MDBox>
        )}
        <MDBox display="flex" alignItems="center">
          {entriesPerPage && (
            <MDBox display="flex" alignItems="center" pr={2}>
              <Autocomplete
                disableClearable
                value={pageSize?.toString()}
                options={entries}
                onChange={(event, newValue) => {
                  onPageSizeChange && onPageSizeChange(parseInt(newValue, 10));
                }}
                size="small"
                sx={{ width: "5rem" }}
                renderInput={(params) => <MDInput {...params} />}
              />
              <MDTypography variant="caption" color="secondary">
                &nbsp;&nbsp;entries per page
              </MDTypography>
            </MDBox>
          )}
          {totalPages > 1 && (
            <MDPagination
              variant={pagination.variant ? pagination.variant : "gradient"}
              color={pagination.color ? pagination.color : "info"}
            >
              {/* Go to first page */}
              {page > 1 && (
                <MDPagination item onClick={() => onPageChange(1)}>
                  <Icon sx={{ fontWeight: "bold" }}>first_page</Icon>
                </MDPagination>
              )}
              {/* Previous page */}
              {page > 1 && (
                <MDPagination item onClick={() => onPageChange(page - 1)}>
                  <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
                </MDPagination>
              )}
              {/* Page numbers, max 5 */}
              {(() => {
                let startPage = Math.max(1, page - 2);
                let endPage = Math.min(totalPages, startPage + 4);
                if (endPage - startPage < 4) {
                  startPage = Math.max(1, endPage - 4);
                }
                const pages = [];
                for (let i = startPage; i <= endPage; i++) {
                  pages.push(
                    <MDPagination
                      item
                      key={i}
                      onClick={() => onPageChange(i)}
                      active={page === i}
                    >
                      {i}
                    </MDPagination>
                  );
                }
                return pages;
              })()}
              {/* Next page */}
              {page < totalPages && (
                <MDPagination item onClick={() => onPageChange(page + 1)}>
                  <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
                </MDPagination>
              )}
              {/* Go to last page */}
              {page < totalPages && (
                <MDPagination item onClick={() => onPageChange(totalPages)}>
                  <Icon sx={{ fontWeight: "bold" }}>last_page</Icon>
                </MDPagination>
              )}
            </MDPagination>
          )}
        </MDBox>
      </MDBox>
    </TableContainer>
  );
}

DataTable.defaultProps = {
  entriesPerPage: { defaultValue: 20, entries: [10, 20, 30, 50] },
  canSearch: false,
  showTotalEntries: true,
  pagination: { variant: "gradient", color: "primary" },
  noEndBorder: false,
  page: 1,
  pageSize: 20,
  totalCount: 0,
  sortBy: "",
  sortType: "asc",
  onPageChange: null,
  onPageSizeChange: null,
  onSortChange: null,
  onSearch: null,
  searchValue: "",

};

DataTable.propTypes = {
  entriesPerPage: PropTypes.oneOfType([
    PropTypes.shape({
      defaultValue: PropTypes.number,
      entries: PropTypes.arrayOf(PropTypes.number),
    }),
    PropTypes.bool,
  ]),
  canSearch: PropTypes.bool,
  showTotalEntries: PropTypes.bool,
  table: PropTypes.objectOf(PropTypes.array).isRequired,
  pagination: PropTypes.shape({
    variant: PropTypes.oneOf(["contained", "gradient"]),
    color: PropTypes.oneOf([
      "primary",
      "secondary",
      "info",
      "success",
      "warning",
      "error",
      "dark",
      "light",
    ]),
  }),
  noEndBorder: PropTypes.bool,
  page: PropTypes.number,
  pageSize: PropTypes.number,
  totalCount: PropTypes.number,
  sortBy: PropTypes.string,
  sortType: PropTypes.oneOf(["asc", "desc"]),
  onPageChange: PropTypes.func,
  onPageSizeChange: PropTypes.func,
  onSortChange: PropTypes.func,
  onSearch: PropTypes.func,
  searchValue: PropTypes.string,
  onClickRow: PropTypes.func,
};

export default DataTable;
